import React, { useEffect, useState } from 'react';
import './App.css';
import { useBranding } from './context/BrandingContext';

import Login from './components/Login';
import DemoPage from './components/DemoPage';
import DemoLoginGate from './components/DemoLoginGate';
import DemoHub from './components/DemoHub';
import AdminDemoGate from './components/AdminDemoGate';
import WhatsAppDemoPage from './components/WhatsAppDemoPage';
import AdminPanel from './components/AdminPanel';
import AsteriskAdminPanel from './components/AsteriskAdminPanel';
import SupervisorDashboard from './components/SupervisorDashboard';
import AgentPortal from './components/AgentPortal';

import api from './services/ApiService';
import PoweredByBadge from './components/PoweredByBadge';
import * as microsoftTeams from '@microsoft/teams-js';
import liveEvents from './services/LiveEvents';
import sipService from './services/SipService';
import { getShell } from './utils/permissions';


function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|;\s*)" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function clearSessionCookies() {
  document.cookie = "ce_token=; max-age=0; SameSite=Lax; Secure";
  document.cookie = "ce_sess=; max-age=0; SameSite=Lax; Secure";
}

function App() {
  // Synchronous session restoration — first render already shows the portal.
  // Falls back to cookies when Teams WebView2 wipes localStorage on tab reload.
  const [session, setSession] = useState<any>(() => {
    let token = localStorage.getItem('token');
    let sess  = localStorage.getItem('ce_session');
    if (!token || !sess) {
      const ct = getCookie('ce_token');
      const cs = getCookie('ce_sess');
      if (ct && cs) {
        try {
          const dec = JSON.parse(decodeURIComponent(atob(cs)));
          token = ct;
          sess  = JSON.stringify(dec);
          localStorage.setItem('token', token);
          localStorage.setItem('ce_session', sess);
        } catch (_) {}
      }
    }
    if (!token || !sess) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('ce_session');
        clearSessionCookies();
        return null;
      }
      const saved = JSON.parse(sess);
      api.setToken(token);
      return saved;
    } catch { return null; }
  });
  const [forcedLogout, setForcedLogout] = useState(false);
  // Read ?tab= from URL so Teams Activity Feed notifications can deep-link
  // straight to the Dialer tab (webUrl in the notification = ?teams=1&tab=Dialer)
  const initialTab = new URLSearchParams(window.location.search).get('tab') || '';


  // Session restored synchronously in useState initializer above
  // Listen for forced logout events pushed from the backend when the user logs
  // in from another device/browser. Clean up immediately so no ghost SIP
  // registrations linger, then show a notification before returning to Login.
  useEffect(() => {
    const handler = () => {
      sipService.unregister();
      liveEvents.disconnect();
      api.clearToken();
      localStorage.removeItem('token');
      localStorage.removeItem('ce_session');
      clearSessionCookies();
      setForcedLogout(true);
    };
    liveEvents.on('force_logout', handler);
    return () => liveEvents.off('force_logout', handler);
  }, []);

  // Auto-redirect to login 8 seconds after forced logout (in case user doesn't click)
  useEffect(() => {
    if (!forcedLogout) return;
    const t = setTimeout(() => {
      setForcedLogout(false);
      setSession(null);
    }, 8000);
    return () => clearTimeout(t);
  }, [forcedLogout]);

  const logout = () => {
    // Revoke server-side session, then clear local state
    api.logout().finally(() => {
      // clearToken() already cleared localStorage + cookies; ensure cookies are gone
      clearSessionCookies();
      setSession(null);
    });
  };


  // Hard-branding: apply the logged-in tenant's own branding on every load
  // (login + session-restore on hard refresh), so it never reverts to the base look.
  const { loadBranding } = useBranding();
  useEffect(() => {
    const tid = session?.user?.tenant_id;
    if (tid) loadBranding(tid);
  }, [session, loadBranding]);

  // Register service worker and subscribe to Web Push after login
  useEffect(() => {
    if (!session) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    const API = '/api';

    async function setupPush() {
      try {
        // Register SW
        const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        await navigator.serviceWorker.ready;

        // Get VAPID public key
        const keyRes = await fetch(API + '/push/vapid-key');
        const { publicKey } = await keyRes.json();
        if (!publicKey) return;

        // Check existing subscription
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          // Request permission and subscribe
          const perm = await Notification.requestPermission();
          if (perm !== 'granted') return;
          const keyBytes = urlBase64ToUint8Array(publicKey);
          sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
        }

        // Send subscription to backend
        await fetch(API + '/push/web-subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('token') },
          body: JSON.stringify(sub.toJSON()),
        });
      } catch (err: any) {
        console.warn('[SW] Push setup failed:', err?.message || String(err));
      }
    }

    function urlBase64ToUint8Array(base64String: string): Uint8Array {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      return Uint8Array.from(Array.from(rawData).map((ch) => ch.charCodeAt(0)));
    }

    setupPush();
  }, [session]);



  // ── Microsoft Teams SDK initialization ────────────────────────────────────
  // When running inside Teams, initialize the SDK so Teams can:
  //   1. Grant notification permission (devicePermissions: ["notifications"] in manifest)
  //   2. Call our onResume handler when the user switches back to this tab
  //      so SIP reconnects instantly rather than waiting for visibilitychange
  useEffect(() => {
    let notified = false;
    function notifyTeams() {
      if (notified) return;
      notified = true;
      try { microsoftTeams.app.notifyAppLoaded(); } catch (_) {}
      try { microsoftTeams.app.notifySuccess(); } catch (_) {}
    }
    const fallback = setTimeout(notifyTeams, 3000);
    microsoftTeams.app.initialize()
      .then(() => {
        clearTimeout(fallback);
        notifyTeams();
        try {
          microsoftTeams.app.lifecycle.registerOnResumeHandler((context: any) => {
            // readyToResume() MUST be called first — removes Teams' blank loading overlay
            // immediately. Without it Teams holds the overlay for ~30-40 s (its own
            // timeout), which also gates all WebView network traffic and prevents the
            // SIP RE-REGISTER from going out until Teams gives up.
            try { context.readyToResume(); } catch (_) {}
            try { sipService.wakeUp(); } catch (_) {}
          });
          microsoftTeams.app.lifecycle.registerBeforeSuspendOrTerminateHandler(async () => {});
        } catch (_) {}
      })
      .catch(() => { clearTimeout(fallback); });
    return () => clearTimeout(fallback);
  }, []);

  // Periodically check if the JWT has expired and force logout if so
  useEffect(() => {
    const interval = setInterval(() => {
      const t = localStorage.getItem('token');
      if (!t) return;
      try {
        const payload = JSON.parse(atob(t.split('.')[1]));
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('token');
          localStorage.removeItem('ce_session');
          clearSessionCookies();
          window.location.reload();
        }
      } catch {}
    }, 5 * 60 * 1000); // check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  if (forcedLogout) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999,
      }}>
        <div style={{
          background: 'white', borderRadius: '20px',
          padding: '40px 36px', maxWidth: '420px', width: '90%',
          textAlign: 'center',
          boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fef3c7', margin: '0 auto 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '32px',
          }}>
            ⚠️
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, color: '#1f2937', marginBottom: '10px' }}>
            Session Ended
          </div>
          <div style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.6', marginBottom: '28px' }}>
            Your account was logged in from another device or browser.<br />
            You have been signed out to protect your account.
          </div>
          <div style={{
            background: '#f1f5f9', borderRadius: '10px',
            padding: '12px 16px', marginBottom: '24px',
            fontSize: '13px', color: '#475569',
          }}>
            You will be automatically redirected to the login page in a few seconds.
          </div>
          <button
            onClick={() => { setForcedLogout(false); setSession(null); }}
            style={{
              background: '#2563eb', color: 'white',
              border: 'none', borderRadius: '10px',
              padding: '14px 32px', fontSize: '15px', fontWeight: 700,
              cursor: 'pointer', width: '100%',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#1d4ed8')}
            onMouseLeave={e => (e.currentTarget.style.background = '#2563eb')}
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Demo page — public, no auth required
  if (window.location.pathname === '/demo') return <DemoPage />;
  if (window.location.pathname === '/whatsapp-demo') return <WhatsAppDemoPage />;

  // demo.callessential.cc routing
  const isDemoHost = window.location.hostname === 'demo.callessential.cc';
  if (isDemoHost && window.location.pathname === '/admindemo') {
    if (!session) return <AdminDemoGate onLogin={setSession} />;
  }
  if (isDemoHost && !session) {
    return (
      <DemoLoginGate>
        <DemoHub onLogin={setSession} />
      </DemoLoginGate>
    );
  }

  if (!session) {
    return <Login onLogin={setSession} />;
  }

  const user = session.user || {};

  // End-users of the internal tenant (1), The Executive Centre (2, since
  // 2026-10-22) and Farhan (383, since 2026-10-29) default to the V2 client.
  // Admins still get the admin console. The V2 app (same origin) picks up the existing session token.
  if (
    [1, 2, 383].includes(Number(user?.tenant_id)) &&
    getShell(user) !== 'admin' &&
    !window.location.pathname.startsWith('/v2')
  ) {
    window.location.replace('/v2/');
    return null;
  }

  let shell: React.ReactNode;
  switch (getShell(user)) {
    case 'admin':
      shell = <AdminPanel key={user.id} user={user} onLogout={logout} />;
      break;
    case 'asterisk_admin':
      shell = <AsteriskAdminPanel key={user.id} user={user} onLogout={logout} />;
      break;
    case 'supervisor':
      shell = (
        <SupervisorDashboard
          key={user.id}
          user={user}
          sipPassword={session.sipPassword}
          sipUsername={session.sipUsername}
          dids={session.dids || []}
          initialTab={initialTab}
          onLogout={logout}
        />
      );
      break;
    default:
      shell = (
        <AgentPortal
          key={user.id}
          user={user}
          sipPassword={session.sipPassword}
          sipUsername={session.sipUsername}
          dids={session.dids || []}
          initialTab={initialTab}
          onLogout={logout}
        />
      );
  }
  return (
    <>
      {shell}
      <PoweredByBadge />
    </>
  );
}

export default App;
